/** Build Twitch VOD/live embed URLs with hostname-aware parent domains. */

export type TwitchPlayerMode = 'vod' | 'live';

export type BuildTwitchPlayerUrlArgs = {
  mode: TwitchPlayerMode;
  videoId?: string | null;
  channelLogin?: string | null;
  parentDomains?: string[];
  autoplay?: boolean;
  muted?: boolean;
  startTimeSeconds?: number;
};

const KNOWN_TWITCH_EMBED_PARENT_DOMAINS = [
  'scoutlytics.gg',
  'www.scoutlytics.gg',
  'stream.scoutlytics.gg',
] as const;

const LOCAL_TWITCH_EMBED_PARENT_DOMAINS = ['localhost', '127.0.0.1'] as const;

const RAW_TWITCH_VOD_ID_PATTERN = /^\d+$/;
const RAW_TWITCH_CHANNEL_LOGIN_PATTERN = /^[a-zA-Z0-9_]{1,25}$/;

function normalizeParentDomain(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    if (trimmed.includes('://')) {
      const hostname = new URL(trimmed).hostname.trim().toLowerCase();
      return hostname || null;
    }
  } catch {
    // Fall through to hostname-only parsing.
  }

  const hostnameOnly = trimmed.split('/')[0]?.split(':')[0]?.trim().toLowerCase();
  return hostnameOnly || null;
}

export function twitchEmbedParent(hostname?: string): string {
  if (hostname) {
    return hostname.trim().toLowerCase();
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname.trim().toLowerCase();
  }
  return 'localhost';
}

export function parseTwitchVodId(urlOrId?: string | null): string | null {
  const trimmed = urlOrId?.trim();
  if (!trimmed) {
    return null;
  }

  if (RAW_TWITCH_VOD_ID_PATTERN.test(trimmed)) {
    return trimmed;
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(trimmed);
  } catch {
    return null;
  }

  const host = parsedUrl.hostname.toLowerCase();
  if (!host.endsWith('twitch.tv')) {
    return null;
  }

  const videosMatch = parsedUrl.pathname.match(/\/videos\/(\d+)/i);
  if (videosMatch?.[1]) {
    return videosMatch[1];
  }

  const channelVideoMatch = parsedUrl.pathname.match(/\/[^/]+\/v\/(\d+)/i);
  if (channelVideoMatch?.[1]) {
    return channelVideoMatch[1];
  }

  return null;
}

export function parseTwitchChannelLogin(urlOrLogin?: string | null): string | null {
  const trimmed = urlOrLogin?.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.includes('/videos/')) {
    return null;
  }

  if (RAW_TWITCH_CHANNEL_LOGIN_PATTERN.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(trimmed);
  } catch {
    return null;
  }

  const host = parsedUrl.hostname.toLowerCase();
  if (!host.endsWith('twitch.tv')) {
    return null;
  }

  const segments = parsedUrl.pathname.split('/').filter(Boolean);
  if (segments.length !== 1) {
    return null;
  }

  const login = segments[0]?.toLowerCase();
  if (!login || login === 'videos' || login === 'directory' || login === 'p') {
    return null;
  }

  return RAW_TWITCH_CHANNEL_LOGIN_PATTERN.test(login) ? login : null;
}

export function resolveTwitchPlayerParentDomains(
  embedParentDomains?: string[] | null
): string[] {
  const parents: string[] = [];
  const seen = new Set<string>();

  const addParent = (value?: string | null) => {
    if (!value) {
      return;
    }
    const normalized = normalizeParentDomain(value);
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    parents.push(normalized);
  };

  const currentHost = twitchEmbedParent();
  addParent(currentHost);

  for (const domain of KNOWN_TWITCH_EMBED_PARENT_DOMAINS) {
    addParent(domain);
  }

  if (LOCAL_TWITCH_EMBED_PARENT_DOMAINS.includes(currentHost as (typeof LOCAL_TWITCH_EMBED_PARENT_DOMAINS)[number])) {
    for (const domain of LOCAL_TWITCH_EMBED_PARENT_DOMAINS) {
      addParent(domain);
    }
  }

  if (Array.isArray(embedParentDomains)) {
    for (const domain of embedParentDomains) {
      addParent(domain);
    }
  }

  return parents;
}

export function buildTwitchPlayerUrl(args: BuildTwitchPlayerUrlArgs): string | null {
  const parentDomains = (args.parentDomains ?? []).filter(Boolean);
  if (parentDomains.length === 0) {
    return null;
  }

  if (args.mode === 'vod') {
    const videoId = args.videoId?.trim();
    if (!videoId) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('video', videoId);
    for (const parent of parentDomains) {
      params.append('parent', parent);
    }
    params.set('autoplay', args.autoplay === true ? 'true' : 'false');
    if (args.muted !== undefined) {
      params.set('muted', args.muted ? 'true' : 'false');
    }
    if (args.startTimeSeconds != null && args.startTimeSeconds > 0) {
      params.set('time', `${Math.floor(args.startTimeSeconds)}s`);
    }

    return `https://player.twitch.tv/?${params.toString()}`;
  }

  const channelLogin = args.channelLogin?.trim().toLowerCase();
  if (!channelLogin) {
    return null;
  }

  const params = new URLSearchParams();
  params.set('channel', channelLogin);
  for (const parent of parentDomains) {
    params.append('parent', parent);
  }
  params.set('autoplay', args.autoplay === true ? 'true' : 'false');
  if (args.muted !== undefined) {
    params.set('muted', args.muted ? 'true' : 'false');
  }

  return `https://player.twitch.tv/?${params.toString()}`;
}

export function buildTwitchVodEmbedUrl(
  providerVodId: string,
  hostname?: string
): string {
  const parentDomains = hostname
    ? resolveTwitchPlayerParentDomains([hostname])
    : resolveTwitchPlayerParentDomains();
  const url = buildTwitchPlayerUrl({
    mode: 'vod',
    videoId: providerVodId,
    parentDomains,
  });
  if (!url) {
    throw new Error('Unable to build Twitch VOD embed URL.');
  }
  return url;
}

export function resolveTwitchParentDomain(
  embedParentDomains?: string[] | null
): string {
  return resolveTwitchPlayerParentDomains(embedParentDomains)[0] ?? twitchEmbedParent();
}

export function resolveTwitchVodEmbedUrl(options: {
  embedUrl?: string | null;
  providerVodId?: string | null;
  embedParentDomains?: string[] | null;
}): string | null {
  const videoId =
    options.providerVodId?.trim() ||
    parseTwitchVodId(options.embedUrl) ||
    parseTwitchVodId(options.providerVodId);

  if (!videoId) {
    return null;
  }

  return buildTwitchPlayerUrl({
    mode: 'vod',
    videoId,
    parentDomains: resolveTwitchPlayerParentDomains(options.embedParentDomains),
  });
}
